import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { deleteCredential, updateCredential } from "../api/credentials";
import { useIsMobile } from "../hooks/useIsMobile";
import { C, font } from "../styles/tokens";
import { deleteLockReason } from "../utils/credentialLock";
import { canRequestChange, pendingEdit } from "../utils/credentialEdit";
import { ViewCredentialModal } from "./ViewCredentialModal";
import { InlineIcon } from "./InlineIcon";
import { SafeLogo } from "./SafeLogo";

/**
 * CredentialList renders structured credential rows (EDUCATION, WORK, or OTHER).
 *
 * Implements strict 5-level information hierarchy:
 *   Level 1: Title & Verification Status Badge Header
 *   Level 2: Primary Issuer / Institution / Company
 *   Level 3: Secondary Metadata (Duration, Field of Study, Location, Grade)
 *   Level 4: Description & Attachments
 *   Level 5: Dedicated Bottom Item Action Bar (Request, Edit, Share, Delete)
 */
export function CredentialList({
  type,
  credentials,
  onChanged,
  emptyHint,
  addLink,
  editLinkPrefix,
}) {
  let items = credentials.filter((c) => c.type === type);

  // Sort WORK experience most recent first
  if (type === "WORK") {
    items = [...items].sort((a, b) => {
      if (a.isCurrent && !b.isCurrent) return -1;
      if (!a.isCurrent && b.isCurrent) return 1;
      const dateA = new Date(a.startDate || a.createdAt || 0);
      const dateB = new Date(b.startDate || b.createdAt || 0);
      return dateB - dateA;
    });
  }

  if (items.length === 0) {
    const addLabel =
      type === "EDUCATION"
        ? "+ Add Education"
        : type === "WORK"
          ? "+ Add Experience"
          : "+ Add Credential";
    return (
      <div
        className="credential-empty-state"
        style={{
          padding: "24px 16px",
          fontSize: 13,
          color: C.textMed,
          textAlign: "center",
          background: "#f8fafc",
          borderRadius: 8,
          border: "1px dashed #cbd5e1",
          margin: "12px 0",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          transition: "all 0.25s ease",
        }}
      >
        <div style={{ marginBottom: 10, color: C.textMed, fontWeight: 500 }}>
          {emptyHint}
        </div>
        <Link
          to={addLink}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 14px",
            fontSize: 12,
            fontWeight: 700,
            color: "#2563eb",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            borderRadius: 6,
            textDecoration: "none",
            fontFamily: font,
          }}
        >
          {addLabel}
        </Link>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 16,
        flex: 1,
        minHeight: "100%",
      }}
    >
      {items.map((c, i) => (
        <CredentialRow
          key={c.id}
          credential={c}
          editLink={`${editLinkPrefix}/${c.id}`}
          onDeleted={onChanged}
          showDivider={i > 0}
        />
      ))}
    </div>
  );
}

function CredentialRow({ credential: c, editLink, onDeleted, showDivider }) {
  const isMobile = useIsMobile();
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [togglingShare, setTogglingShare] = useState(false);
  const [error, setError] = useState(null);

  const hiddenFromShare = c.visibleOnShare === false;
  const deleteLocked = deleteLockReason(c.status);

  async function handleToggleShare() {
    setTogglingShare(true);
    setError(null);
    try {
      await updateCredential(c.id, { visibleOnShare: hiddenFromShare });
      onDeleted?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err.message ??
          "Could not update visibility",
      );
    } finally {
      setTogglingShare(false);
    }
  }

  async function handleConfirmDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteCredential(c.id);
      setShowDeleteModal(false);
      onDeleted?.();
    } catch (err) {
      setDeleteError(
        err?.response?.data?.message ?? err.message ?? "Could not delete",
      );
      setDeleting(false);
    }
  }

  const dateRange = formatDateRange(c.startDate, c.endDate, c.isCurrent);

  return (
    <div
      id={`credential-${c.id}`}
      style={{
        paddingTop: showDivider ? 16 : 4,
        borderTop: showDivider ? "1px solid #e2e8f0" : "none",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        gap: 8,
      }}
    >
      {/* Top Body Content */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {/* Level 1 -- Primary Title & Status Badge Row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 12,
            width: "100%",
          }}
        >
          <h3
            onClick={() => setShowViewModal(true)}
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: C.navy,
              margin: 0,
              lineHeight: 1.35,
              flex: 1,
              wordBreak: "break-word",
              cursor: "pointer",
            }}
            title="Click to view details"
            className="hover:underline hover:text-blue-600 transition-colors"
          >
            {c.title}
          </h3>
          <StatusBadge status={c.status} />
          {pendingEdit(c) && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 text-[10px] font-bold uppercase tracking-wide">
              Change with verifier
            </span>
          )}
        </div>

        {/* Level 2 -- Issuer / Institution / Company */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            marginTop: 2,
          }}
        >
          <SafeLogo
            src={c.issuerLogoUrl}
            alt={c.issuer || "Issuer"}
            fallbackText={c.issuer ? c.issuer.slice(0, 2).toUpperCase() : "TP"}
            imgStyle={{
              width: 28,
              height: 28,
              borderRadius: 6,
              objectFit: "contain",
              border: "1px solid #e2e8f0",
              background: "#ffffff",
              padding: 2,
              flexShrink: 0,
            }}
            fallbackStyle={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "#f1f5f9",
              border: "1px solid #e2e8f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              fontSize: 11,
              fontWeight: 700,
              flexShrink: 0,
            }}
          />
          <div
            style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
          >
            <div style={{ fontSize: 13.5, fontWeight: 600, color: "#334155" }}>
              {c.issuer}
            </div>
            {c.issuerDescription && (
              <div
                style={{
                  fontSize: 11.5,
                  color: "#64748b",
                  fontWeight: 400,
                  marginTop: 1,
                  lineHeight: 1.3,
                }}
              >
                {c.issuerDescription}
              </div>
            )}
          </div>
        </div>

        {/* Level 3 -- Secondary Metadata & Duration Chips */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            fontSize: 12,
            color: C.textMed,
            marginTop: 2,
          }}
        >
          {dateRange && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "3px 9px",
                borderRadius: 6,
                fontWeight: 500,
                color: "#475569",
              }}
            >
              {dateRange}
            </span>
          )}
          {c.metadata?.fieldOfStudy && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "3px 9px",
                borderRadius: 6,
                fontWeight: 500,
                color: "#475569",
              }}
            >
              {c.metadata.fieldOfStudy}
              {c.metadata.grade && ` - Grade: ${c.metadata.grade}`}
            </span>
          )}
          {(c.metadata?.enrollmentNo || c.metadata?.registrationNo) && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "3px 9px",
                borderRadius: 6,
                fontWeight: 500,
                color: "#475569",
              }}
            >
              {c.metadata.enrollmentNo
                ? `Enrolment no. ${c.metadata.enrollmentNo}`
                : `Registration no. ${c.metadata.registrationNo}`}
            </span>
          )}
          {c.metadata?.location && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "3px 9px",
                borderRadius: 6,
                fontWeight: 500,
                color: "#475569",
              }}
            >
              {c.metadata.location}
              {c.metadata.employmentType && ` (${c.metadata.employmentType})`}
            </span>
          )}
          {(c.metadata?.reportingManagerName ||
            c.metadata?.reportingManagerDesignation) && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                padding: "3px 9px",
                borderRadius: 6,
                fontWeight: 500,
                color: "#475569",
              }}
            >
              Manager:{" "}
              {c.metadata.reportingManagerName &&
              c.metadata.reportingManagerDesignation
                ? `${c.metadata.reportingManagerName} (${c.metadata.reportingManagerDesignation})`
                : c.metadata.reportingManagerName ||
                  c.metadata.reportingManagerDesignation}
            </span>
          )}
        </div>

        {/* Level 4 -- Description & File Attachment */}
        {c.description && (
          <div
            style={{
              fontSize: 13,
              color: C.textMed,
              lineHeight: 1.5,
              marginTop: 4,
              whiteSpace: "pre-line",
            }}
          >
            {c.description}
          </div>
        )}

        {c.certificateS3Key && (
          <div
            style={{
              fontSize: 12,
              color: C.textLight,
              display: "flex",
              alignItems: "center",
              gap: 5,
              marginTop: 4,
            }}
          >
            <PaperclipIcon />
            <span style={{ color: "#2563eb", fontWeight: 600 }}>
              {c.certificateFileName || "Certificate attached"}
            </span>
          </div>
        )}

        {error && (
          <div style={{ fontSize: 12, color: C.red, marginTop: 2 }}>
            {error}
          </div>
        )}
      </div>

      {/* Level 5 -- Item Action Bar (Fixed Bottom Alignment) */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: isMobile ? "wrap" : "nowrap",
          gap: isMobile ? 8 : 6,
          marginTop: "auto",
          paddingTop: 12,
          borderTop: "1px dashed #e2e8f0",
        }}
      >
        {/* Verification Request Button (If unverified or rejected) */}
        {c.status === "NONE" || c.status === "REJECTED" ? (
          <Link
            to={`/verify/request?credentialId=${c.id}`}
            style={{
              ...actionLinkStyle,
              background: "#2563eb",
              color: "white",
              border: "1px solid #2563eb",
              borderRadius: 6,
              fontWeight: 700,
              padding: isMobile ? "8px 12px" : "4px 8px",
              minHeight: isMobile ? 38 : undefined,
              fontSize: isMobile ? 12 : 11,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: isMobile ? "100%" : "auto",
            }}
          >
            Request verification
          </Link>
        ) : (
          !isMobile && <div />
        )}

        {/* Right Action Group: View, Edit, Hide/Show Share, Delete (Fixed Right Alignment) */}
        <div
          style={{
            display: "flex",
            gap: 6,
            alignItems: "center",
            marginLeft: isMobile ? 0 : "auto",
            width: isMobile ? "100%" : "auto",
            justifyContent: isMobile ? "space-between" : "flex-end",
            flexShrink: 0,
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}
        >
          {/* View Action Button - available for all credentials */}
          <button
            type="button"
            onClick={() => setShowViewModal(true)}
            title="View details"
            style={{
              ...actionButtonStyle,
              color: "#0f766e",
              background: "#f0fdfa",
              border: "1px solid #99f6e4",
              borderRadius: 6,
              padding: isMobile ? "8px 12px" : "4px 8px",
              minHeight: isMobile ? 38 : undefined,
              fontSize: isMobile ? 12 : 11,
              flex: isMobile ? 1 : "initial",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              cursor: "pointer",
            }}
          >
            <InlineIcon name="eye" size={12} />
            <span>View</span>
          </button>

          {/* Edit Action Button - accessible for all credentials */}
          <Link
            to={editLink}
            title={
              c.status === "VERIFIED"
                ? "Edit additional info"
                : "Edit credential"
            }
            style={{
              ...actionLinkStyle,
              color: "#2563eb",
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              padding: isMobile ? "8px 12px" : "4px 8px",
              minHeight: isMobile ? 38 : undefined,
              fontSize: isMobile ? 12 : 11,
              flex: isMobile ? 1 : "initial",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Edit
          </Link>

          {/* Change Verified Details - when verified and no pending review */}
          {canRequestChange(c) && (
            <Link
              to={`${editLink}?change=1`}
              title="Change verified details (fee applies)"
              style={{
                ...actionLinkStyle,
                color: "#7c3aed",
                background: "#f5f3ff",
                border: "1px solid #ddd6fe",
                borderRadius: 6,
                padding: isMobile ? "8px 12px" : "4px 8px",
                minHeight: isMobile ? 38 : undefined,
                fontSize: isMobile ? 12 : 11,
                flex: isMobile ? 1 : "initial",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Change
            </Link>
          )}

          <button
            type="button"
            onClick={handleToggleShare}
            disabled={togglingShare}
            title={hiddenFromShare ? "Show on share" : "Hide from share"}
            style={{
              ...actionButtonStyle,
              color: hiddenFromShare ? C.amber : C.textMed,
              background: hiddenFromShare ? C.amberLight : "#f8fafc",
              border: `1px solid ${hiddenFromShare ? "#fcd34d" : "#cbd5e1"}`,
              borderRadius: 6,
              padding: isMobile ? "8px 12px" : "4px 8px",
              minHeight: isMobile ? 38 : undefined,
              fontSize: isMobile ? 12 : 11,
              flex: isMobile ? 1.4 : "initial",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {togglingShare
              ? "..."
              : hiddenFromShare
                ? "Show on share"
                : "Hide from share"}
          </button>

          <button
            type="button"
            onClick={() => {
              setDeleteError(null);
              setShowDeleteModal(true);
            }}
            disabled={deleting || Boolean(deleteLocked)}
            title={deleteLocked ?? "Delete record"}
            style={{
              ...actionButtonStyle,
              color: deleteLocked ? C.textLight : C.red,
              background: deleteLocked ? "#f1f5f9" : "#fef2f2",
              border: `1px solid ${deleteLocked ? "#cbd5e1" : "#fecaca"}`,
              borderRadius: 6,
              cursor: deleteLocked ? "not-allowed" : "pointer",
              opacity: deleteLocked ? 0.6 : 1,
              padding: isMobile ? "8px 12px" : "4px 8px",
              minHeight: isMobile ? 38 : undefined,
              fontSize: isMobile ? 12 : 11,
              flex: isMobile ? 1 : "initial",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Delete
          </button>
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          if (!deleting) {
            setShowDeleteModal(false);
            setDeleteError(null);
          }
        }}
        onConfirm={handleConfirmDelete}
        itemName={c.title}
        type={c.type}
        deleting={deleting}
        error={deleteError}
      />

      <ViewCredentialModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        credential={c}
        editLink={editLink}
      />
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    NONE: { bg: "#f1f5f9", color: C.textMed, label: "Unverified" },
    PENDING: { bg: C.amberLight, color: C.amber, label: "Pending" },
    VERIFIED: { bg: C.greenLight, color: C.green, label: "Verified" },
    REJECTED: { bg: C.redLight, color: C.red, label: "Rejected" },
  }[status] ?? { bg: "#f1f5f9", color: C.textMed, label: status };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 9px",
        fontSize: 10,
        fontWeight: 700,
        color: config.color,
        background: config.bg,
        borderRadius: 10,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: "50%",
          background: config.color,
        }}
      />
      {config.label}
    </span>
  );
}

function formatDateRange(start, end, isCurrent) {
  if (!start && !end) return null;
  const fmt = (d) => {
    if (!d) return "";
    const date = new Date(d);
    return date.toLocaleDateString("en-IN", {
      month: "short",
      year: "numeric",
    });
  };
  const startStr = fmt(start);
  const endStr = isCurrent ? "Present" : fmt(end);
  if (startStr && endStr) return `${startStr} - ${endStr}`;
  return startStr || endStr;
}

function PaperclipIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </svg>
  );
}

const actionBaseStyle = {
  fontSize: 11,
  fontWeight: 600,
  padding: "4px 8px",
  fontFamily: font,
  minHeight: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  boxSizing: "border-box",
};

const actionLinkStyle = {
  ...actionBaseStyle,
  textDecoration: "none",
};

const actionButtonStyle = {
  ...actionBaseStyle,
};

function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  itemName,
  type,
  deleting,
  error,
}) {
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e) {
      if (e.key === "Escape" && !deleting) {
        onClose();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, deleting, onClose]);

  if (!isOpen) return null;

  const typeLabel =
    type === "WORK"
      ? "Experience"
      : type === "EDUCATION"
        ? "Education"
        : "Credential";

  const modalContent = (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        backgroundColor: "rgba(15, 23, 42, 0.6)",
        backdropFilter: "blur(4px)",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !deleting) {
          onClose();
        }
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          backgroundColor: "#ffffff",
          borderRadius: 16,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          border: `1px solid ${C.divider}`,
          overflow: "hidden",
          fontFamily: font,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px 14px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                backgroundColor: C.redLight,
                color: C.red,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
            </div>
            <div>
              <h3
                style={{
                  fontSize: 17,
                  fontWeight: 700,
                  color: C.navy,
                  margin: 0,
                }}
              >
                Delete {typeLabel}?
              </h3>
              <p
                style={{
                  fontSize: 12,
                  color: C.textMed,
                  margin: "2px 0 0",
                }}
              >
                This action cannot be undone
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            style={{
              background: "transparent",
              border: "none",
              cursor: deleting ? "not-allowed" : "pointer",
              color: C.textLight,
              padding: 4,
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "0 24px 20px" }}>
          <p
            style={{
              fontSize: 14,
              color: C.textDark,
              lineHeight: 1.5,
              margin: 0,
            }}
          >
            Are you sure you want to delete{" "}
            <strong>"{itemName || "this item"}"</strong>? This will be
            permanently removed from your profile.
          </p>

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 8,
                color: C.red,
                fontSize: 12,
                lineHeight: 1.4,
              }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Actions Footer */}
        <div
          style={{
            padding: "14px 24px",
            backgroundColor: "#f8fafc",
            borderTop: `1px solid ${C.divider}`,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #cbd5e1",
              backgroundColor: "#ffffff",
              color: C.textDark,
              fontSize: 13,
              fontWeight: 600,
              cursor: deleting ? "not-allowed" : "pointer",
              transition: "background 0.15s ease",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            style={{
              padding: "8px 18px",
              borderRadius: 8,
              border: "none",
              backgroundColor: C.red,
              color: "#ffffff",
              fontSize: 13,
              fontWeight: 600,
              cursor: deleting ? "not-allowed" : "pointer",
              opacity: deleting ? 0.6 : 1,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              transition: "opacity 0.15s ease",
            }}
          >
            {deleting ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : modalContent;
}
